import React, { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '../../../components/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { importService } from '../services/importService';
import { useAuth } from '../../auth';

interface SyncAccount {
  accountId: string;
  accountName: string;
  accountNumber: string;
  adjustTimezone?: boolean;
  monthsBack?: number;
  lastSyncAt: string | null;
}

const POLL_INTERVAL = 5_000; // 5 segundos

const ImportPage: React.FC = () => {
  const { user } = useAuth();
  const [token, setToken] = useState(() => localStorage.getItem('fin_pluggy_token') || '');
  const [accounts, setAccounts] = useState<SyncAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState<{ accountId: string; value: string } | null>(null);
  const [newAccountId, setNewAccountId] = useState('');
  const [isImporting, setIsImporting] = useState(() => localStorage.getItem('fin_importing') === 'true');
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  // Polling do status de importação
  const checkImportStatus = useCallback(async () => {
    try {
      const res = await importService.getImportStatus();
      const status = res.data.data;
      setIsImporting(status.isImporting);
      localStorage.setItem('fin_importing', String(status.isImporting));
      return status.isImporting as boolean;
    } catch {
      return false;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const stillImporting = await checkImportStatus();
      if (!stillImporting && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, POLL_INTERVAL);
  }, [checkImportStatus]);

  // Verificar status ao montar E quando a aba/página ganha foco
  useEffect(() => {
    let active = true;

    const init = async () => {
      const importing = await checkImportStatus();
      if (active && importing) {
        startPolling();
      }
    };

    init();

    // Re-verificar ao voltar para a aba ou ao focar a janela
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        init();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', handleVisibility);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [checkImportStatus, startPolling]);

  useEffect(() => {
    importService.listAccounts()
      .then(res => setAccounts(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  const handleTokenChange = (v: string) => {
    setToken(v);
    localStorage.setItem('fin_pluggy_token', v);
  };

  const handleSyncAll = async () => {
    if (!token.trim()) { setMessage('Informe o token'); return; }
    setSyncing('all');
    setMessage('');
    try {
      const res = await importService.syncAll(token);
      setMessage(`Todas sincronizadas: ${res.data.data.synced} transações`);
      const listRes = await importService.listAccounts();
      setAccounts(listRes.data.data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setMessage(e.response?.data?.error || 'Erro na sincronização');
    } finally {
      setSyncing(null);
    }
  };

  const handleQueueSync = async () => {
    if (!user?.email) { setMessage('Usuário não autenticado'); return; }
    setSyncing('queue');
    setMessage('');
    try {
      const res = await importService.queueSync(user.email);
      setMessage(res.data.data.message || 'Sincronização solicitada com sucesso');
      setIsImporting(true);
      localStorage.setItem('fin_importing', 'true');
      startPolling();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setMessage(e.response?.data?.error || 'Erro ao solicitar sincronização');
    } finally {
      setSyncing(null);
    }
  };

  const handleEditBlur = async () => {
    if (editing?.value.trim() && editing.value !== accounts.find(a => a.accountId === editing.accountId)?.accountName) {
      await importService.updateAccountName(editing.accountId, editing.value.trim());
      setAccounts(prev => prev.map(a => a.accountId === editing.accountId ? { ...a, accountName: editing.value.trim() } : a));
    }
    setEditing(null);
  };

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const handleRemoveAccount = async (accountId: string) => {
    await importService.removeAccount(accountId);
    setAccounts(prev => prev.filter(a => a.accountId !== accountId));
    setConfirmDelete(null);
  };

  const handleSync = async (accountId: string) => {
    if (!token.trim()) { setMessage('Informe o token'); return; }
    setSyncing(accountId);
    setMessage('');
    try {
      const res = await importService.syncAccount(accountId, token);
      setMessage(`Sincronizado: ${res.data.data.synced} transações`);
      // Update lastSyncAt locally
      setAccounts(prev => prev.map(a => a.accountId === accountId ? { ...a, lastSyncAt: new Date().toISOString() } : a));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setMessage(e.response?.data?.error || 'Erro na sincronização');
    } finally {
      setSyncing(null);
    }
  };

  const handleToggleTimezone = (accountId: string, checked: boolean) => {
    importService.updateAdjustTimezone(accountId, checked);
    setAccounts(prev => prev.map(a => a.accountId === accountId ? { ...a, adjustTimezone: checked } : a));
  };

  const handleMonthsBack = async (accountId: string, value: number) => {
    try {
      await importService.updateMonthsBack(accountId, value);
      setAccounts(prev => prev.map(a => a.accountId === accountId ? { ...a, monthsBack: value } : a));
    } catch { /* ignore */ }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="relative min-h-full">
      {isImporting && (
        <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 flex items-start pt-20 sm:items-center justify-center rounded-lg">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm mx-4 text-center">
            <div className="mb-4 flex justify-center">
              <div className="w-12 h-12 relative">
                <div className="absolute inset-0 border-4 border-green-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-green-600 rounded-full border-t-transparent animate-spin"></div>
              </div>
            </div>
            <p className="text-gray-700 font-medium text-sm">Importação em andamento... Aguarde a sincronização finalizar.</p>
          </div>
        </div>
      )}
      <PageHeader title="Sincronização" />
      <div className="px-0 py-4 sm:px-2 space-y-4">
        {/* Sincronização */}
        <div className="sm:max-w-2xl sm:mx-auto space-y-3">
          {/* Automático */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">Sincronização Automática</p>
              <p className="text-xs text-blue-600">Envia para fila e sincroniza via Playwright ({user?.email})</p>
            </div>
            <button
              onClick={handleQueueSync}
              disabled={syncing !== null || isImporting}
              className="w-full sm:w-auto btn-primary flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
            >
              <svg className={`w-4 h-4 ${syncing === 'queue' ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Sincronizar Todos
            </button>
          </div>

          {/* Manual */}
          <div className="flex flex-col sm:flex-row gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex-1">
              <label htmlFor="pluggy-token" className="block text-xs font-medium text-gray-600 mb-1">Sync Manual (Token Pluggy)</label>
              <input
                id="pluggy-token"
                type="password"
                value={token}
                onChange={e => handleTokenChange(e.target.value)}
                placeholder="Cole o Bearer token aqui..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleSyncAll}
              disabled={syncing !== null || !token.trim() || isImporting}
              className="sm:self-end w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
            >
              <svg className={`w-4 h-4 ${syncing === 'all' ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Sincronizar Manual
            </button>
          </div>
        </div>

        {/* Add account ID */}
        <div className="flex flex-col sm:flex-row gap-2 sm:max-w-2xl sm:mx-auto">
          <div className="flex-1">
            <label htmlFor="new-account-id" className="block text-xs font-medium text-gray-700 mb-1">ID da Conexão (Pluggy)</label>
            <input
              id="new-account-id"
              value={newAccountId}
              onChange={e => setNewAccountId(e.target.value)}
              placeholder="Cole o accountId aqui..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={async () => { if (!newAccountId.trim()) { return; } await importService.addAccount(newAccountId.trim()); setNewAccountId(''); const res = await importService.listAccounts(); setAccounts(res.data.data); }}
            disabled={!newAccountId.trim()}
            className="sm:self-end w-full sm:w-auto btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
          >
            Adicionar
          </button>
        </div>

        {message && (
          <div className={`text-sm px-3 py-2 rounded-lg sm:max-w-2xl sm:mx-auto ${message.includes('Erro') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {message}
          </div>
        )}

        {/* Mobile Cards */}
        <div className="sm:hidden space-y-2">
          {accounts.map(acc => (
            <div key={acc.accountId} className="border rounded-lg bg-white p-3 space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {editing?.accountId === acc.accountId ? (
                    <input autoFocus value={editing.value} onChange={e => setEditing({ ...editing, value: e.target.value })}
                      onBlur={handleEditBlur}
                      onKeyDown={e => { if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); } if (e.key === 'Escape') { setEditing(null); } }}
                      className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  ) : (
                    <button type="button" className="text-sm font-medium text-gray-800 text-left truncate block w-full" onClick={() => setEditing({ accountId: acc.accountId, value: acc.accountName || '' })}>
                      {acc.accountName || '—'}
                    </button>
                  )}
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    <span>Nº {acc.accountNumber || '—'}</span>
                    <span>{acc.lastSyncAt ? new Date(acc.lastSyncAt).toLocaleString('pt-BR') : 'Nunca sincronizado'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <label className="flex items-center gap-1 cursor-pointer text-xs text-gray-500">
                    <input type="checkbox" checked={acc.adjustTimezone !== false}
                      onChange={e => handleToggleTimezone(acc.accountId, e.target.checked)}
                      className="rounded w-3 h-3" />{' '}
                    -3h
                  </label>
                  <select value={acc.monthsBack ?? 2} onChange={e => handleMonthsBack(acc.accountId, Number(e.target.value))}
                    className="border border-gray-300 rounded px-1 py-0.5 text-xs text-gray-600">
                    <option value={1}>1m</option>
                    <option value={2}>2m</option>
                    <option value={3}>3m</option>
                    <option value={6}>6m</option>
                    <option value={12}>12m</option>
                    <option value={0}>Todos</option>
                  </select>
                </div>
              </div>
              <div className="text-xs text-gray-400 font-mono">{acc.accountId}</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => handleSync(acc.accountId)} disabled={syncing !== null || isImporting}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium border rounded-lg ${syncing === acc.accountId ? 'text-blue-600 border-blue-300 animate-pulse' : 'text-blue-600 border-blue-300 hover:bg-blue-50'}`}>
                  <svg className={`w-4 h-4 ${syncing === acc.accountId ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Sincronizar
                </button>
                <button onClick={() => setConfirmDelete(acc.accountId)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="hidden sm:flex sm:flex-col sm:items-center">
          <div className="w-fit max-w-full">
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 text-xs">ID</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Conta</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Número</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Última Sync</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-600 whitespace-nowrap">-3h</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-600 whitespace-nowrap">Meses</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-600 w-16">Sync</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map(acc => (
                      <tr key={acc.accountId} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-xs text-gray-500 font-mono">{acc.accountId}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {editing?.accountId === acc.accountId ? (
                            <input autoFocus value={editing.value} onChange={e => setEditing({ ...editing, value: e.target.value })}
                              onBlur={handleEditBlur}
                              onKeyDown={e => { if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); } if (e.key === 'Escape') { setEditing(null); } }}
                              className="border border-blue-300 rounded px-2 py-0.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500" />
                          ) : (
                            <button type="button" className="text-left hover:text-blue-600" onDoubleClick={() => setEditing({ accountId: acc.accountId, value: acc.accountName || '' })}>
                              {acc.accountName || '—'}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2">{acc.accountNumber || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                          {acc.lastSyncAt ? new Date(acc.lastSyncAt).toLocaleString('pt-BR') : 'Nunca'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={acc.adjustTimezone !== false}
                            onChange={e => handleToggleTimezone(acc.accountId, e.target.checked)}
                            className="rounded" title="Ajustar fuso -3h" />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <select value={acc.monthsBack ?? 2} onChange={e => handleMonthsBack(acc.accountId, Number(e.target.value))}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs" title="Meses para trás">
                            <option value={1}>1m</option>
                            <option value={2}>2m</option>
                            <option value={3}>3m</option>
                            <option value={6}>6m</option>
                            <option value={12}>12m</option>
                            <option value={0}>Todos</option>
                          </select>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => handleSync(acc.accountId)}
                            disabled={syncing !== null || isImporting}
                            className={`p-1.5 rounded-full transition-colors ${syncing === acc.accountId ? 'animate-spin text-blue-600' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                            title="Sincronizar">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => setConfirmDelete(acc.accountId)} className="text-red-400 hover:text-red-600 p-1" title="Remover">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Confirmar exclusão</h3>
            <p className="text-sm text-gray-600">Todas as transações associadas a esta conta serão apagadas permanentemente.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button onClick={() => handleRemoveAccount(confirmDelete)} className="flex-1 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportPage;
